import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

const OnboardingStep5 = () => {
  const navigate = useNavigate();
  const [referralSource, setReferralSource] = useState('');
  const [referralSourceDetail, setReferralSourceDetail] = useState('');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContinue = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error('No authenticated user');
        return;
      }

      // Mark step 5 as complete AND mark onboarding as complete
      // This will trigger the welcome email and notification via Cloud Function
      const updateData = {
        step5Complete: true,
        onboardingComplete: true
      };

      // Add referral source if selected (optional)
      if (referralSource) {
        updateData.referralSource = referralSource;
        // Add detail if "Other" was selected
        if (referralSource === 'Other' && referralSourceDetail) {
          updateData.referralSourceDetail = referralSourceDetail.trim();
        }
      }

      await updateDoc(doc(db, 'users', user.uid), updateData);

      // Navigate to Step 6 (now optional)
      navigate('/onboarding-step-6');
    } catch (error) {
      console.error('Error completing step 5:', error);
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 1024px) {
          body {
            padding-bottom: 0 !important;
          }
          .onboarding-container {
            min-height: calc(100vh - var(--navbar-height, 125px)) !important;
            padding-bottom: 120px !important;
          }
        }
      `}</style>
      <div className="onboarding-container" style={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5', 
        padding: '2rem 1rem',
        display: 'flex',
        flexDirection: 'column',
      alignItems: 'center'
    }}>
      <div style={{ 
        maxWidth: '1400px', 
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '3rem 2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        boxSizing: 'border-box'
      }}
      className="onboarding-step5-container">
        {/* Header */}
        <h1 style={{
          fontFamily: 'Nunito, sans-serif',
          fontSize: '2rem',
          fontWeight: '700',
          color: '#4A5759',
          textAlign: 'center',
          marginBottom: '3.5rem',
          lineHeight: '1.4'
        }}>
          Nicely done! Welcome to the smarter, safer, and more affordable<br className="desktop-break" /> way to discover new gear.
        </h1>
        <style>{`
          @media (max-width: 768px) {
            .desktop-break {
              display: none;
            }
          }
        `}</style>

        {/* How It Works Section - Identical to Dashboard */}
        <div style={{ marginBottom: '3rem', marginTop: '1rem' }}>
          <h2 style={{
            fontFamily: 'Nunito, sans-serif',
            fontSize: '1.8125rem',
            fontWeight: '700',
            color: '#4A5759',
            textAlign: 'center',
            marginBottom: '1rem'
          }}>
            Here's How It Works
          </h2>

          <div className="how-it-works-steps" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2rem',
            justifyContent: 'center',
            alignItems: 'flex-start'
          }}>
            {/* Step 1 */}
            <div style={{
              flex: '1 1 280px',
              maxWidth: '320px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <div className="how-it-works-image-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '260px' }}>
                <img 
                  src="/images/howitworkspedal.svg" 
                  alt="List Pedals" 
                  style={{ width: '280px', height: '280px', maxWidth: '100%' }}
                />
              </div>
              <h3 style={{
                fontFamily: 'Nunito, sans-serif',
                fontSize: '1.40625rem',
                fontWeight: '600',
                color: '#4A5759',
                marginBottom: '0.75rem'
              }}>
                List your pedals
              </h3>
              <p style={{
                fontFamily: 'Nunito, sans-serif',
                fontSize: '1.15625rem',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                List the pedals you wish to trade or sell. Then heart the pedals you're after to build your Wantlist, and we'll tell you when one is listed.
              </p>
            </div>

            {/* Step 2 */}
            <div style={{
              flex: '1 1 280px',
              maxWidth: '320px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <div className="how-it-works-image-wrapper match-svg-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '260px' }}>
                <img 
                  src="/images/howitworksmatch.svg" 
                  alt="Review Matches" 
                  style={{ width: '373px', height: '373px', maxWidth: '100%' }}
                />
              </div>
              <h3 style={{
                fontFamily: 'Nunito, sans-serif',
                fontSize: '1.40625rem',
                fontWeight: '600',
                color: '#4A5759',
                marginBottom: '0.75rem'
              }}>
                Browse & send offers
              </h3>
              <p style={{
                fontFamily: 'Nunito, sans-serif',
                fontSize: '1.15625rem',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                Browse listings and send offers on gear that excites you. Most trades start with a direct offer. If your Wantlist ever overlaps with another member's Pedalboard, we'll show you the trade match too.
              </p>
            </div>

            {/* Step 3 */}
            <div style={{
              flex: '1 1 280px',
              maxWidth: '320px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <div className="how-it-works-image-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '260px' }}>
                <img 
                  src="/images/howitworkstrade.svg" 
                  alt="Ship & Trade" 
                  style={{ width: '308px', height: '308px', maxWidth: '100%' }}
                />
              </div>
              <h3 style={{
                fontFamily: 'Nunito, sans-serif',
                fontSize: '1.40625rem',
                fontWeight: '600',
                color: '#4A5759',
                marginBottom: '0.75rem'
              }}>
                Ship & trade
              </h3>
              <p style={{
                fontFamily: 'Nunito, sans-serif',
                fontSize: '1.15625rem',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                Choose the best offer, whether trade or cash, purchase your fully insured shipping label through the platform, and safely exchange gear using our streamlined, secure process.
              </p>
            </div>
          </div>
        </div>

        {/* Referral Source Dropdown */}
        <div style={{
          maxWidth: '500px',
          margin: '3rem auto 0',
          textAlign: 'center'
        }}>
          <label style={{
            fontFamily: 'Nunito, sans-serif',
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#4A5759',
            display: 'block',
            marginBottom: '0.75rem'
          }}>
            How did you discover us? (optional)
          </label>
          <select
            value={referralSource}
            onChange={(e) => setReferralSource(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              fontFamily: 'Nunito, sans-serif',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#4A5759',
              cursor: 'pointer',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#C9A227';
              e.target.style.outline = 'none';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
            }}
          >
            <option value="">Select an option</option>
            <option value="Reddit">Reddit</option>
            <option value="Facebook">Facebook</option>
            <option value="Google">Google</option>
            <option value="Instagram">Instagram</option>
            <option value="YouTube">YouTube</option>
            <option value="Word of Mouth">Word of Mouth</option>
            <option value="Other">Other</option>
          </select>

          {/* Show text input when "Other" is selected */}
          {referralSource === 'Other' && (
            <input
              type="text"
              placeholder="Please tell us where you heard about us..."
              value={referralSourceDetail}
              onChange={(e) => setReferralSourceDetail(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                fontSize: '1rem',
                fontFamily: 'Nunito, sans-serif',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#4A5759',
                marginTop: '0.75rem',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#C9A227';
                e.target.style.outline = 'none';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
              }}
            />
          )}
        </div>

        {/* Ready to find text */}
        <h2 style={{
          fontFamily: 'Nunito, sans-serif',
          fontSize: '1.75rem',
          fontWeight: '700',
          color: '#4A5759',
          textAlign: 'center',
          marginTop: '3rem',
          marginBottom: '2rem',
          lineHeight: '1.4'
        }}>
          Ready to find your new favorite pedal?
        </h2>

        {/* Continue Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleContinue}
            style={{
              backgroundColor: '#C9A227',
              color: 'white',
              padding: '0.875rem 3rem',
              border: 'none',
              borderRadius: '9999px',
              fontSize: '1.125rem',
              fontWeight: 'bold',
              fontFamily: 'Nunito, sans-serif',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(201, 162, 39, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#b8912a';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(201, 162, 39, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#C9A227';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(201, 162, 39, 0.3)';
            }}
          >
            Complete Registration
          </button>
        </div>

        {/* Mobile responsive styles */}
        <style>
          {`
            .how-it-works-image-wrapper {
              margin-bottom: -2rem;
            }
            
            @media (min-width: 769px) {
              .how-it-works-steps {
                gap: 6rem !important;
                justify-content: space-between !important;
                padding: 0 2rem !important;
              }
            }
            
            @media (max-width: 768px) {
              .onboarding-step5-container {
                padding: 2rem 0.75rem !important;
              }
              .how-it-works-image-wrapper {
                margin-bottom: -4rem !important;
                height: auto !important;
              }
              .match-svg-wrapper {
                margin-top: -2.5rem !important;
                margin-bottom: -6rem !important;
              }
              .how-it-works-steps {
                gap: 0rem !important;
              }
              body {
                overflow-x: hidden;
              }
            }
          `}
        </style>
      </div>
    </div>
    </>
  );
};

export default OnboardingStep5;

